import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, CheckCircle2, Download, Gauge, QrCode, RefreshCcw, Share, Smartphone } from "lucide-react";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";
import { canPromptInstall, isIOS, isStandaloneApp, promptInstall, subscribeInstall } from "../utils/pwa";

export default function AppDownload() {
  usePageMeta("appPage.title", "appPage.sub");
  const { t } = useLanguage();
  const [, force] = useState(0);
  useEffect(() => subscribeInstall(() => force((n) => n + 1)), []);

  const standalone = isStandaloneApp();
  const promptable = canPromptInstall();
  const ios = isIOS();

  const perks = [
    [<Smartphone key="1" className="w-6 h-6" />, t("appPage.p1t"), t("appPage.p1d")],
    [<RefreshCcw key="2" className="w-6 h-6" />, t("appPage.p2t"), t("appPage.p2d")],
    [<Gauge key="3" className="w-6 h-6" />, t("appPage.p3t"), t("appPage.p3d")],
    [<BellRing key="4" className="w-6 h-6" />, t("appPage.p4t"), t("appPage.p4d")],
    [<QrCode key="5" className="w-6 h-6" />, t("appPage.p5t"), t("appPage.p5d")],
  ];

  return (
    <div data-testid="app-page">
      {/* HERO scuro con installazione PWA */}
      <section className="bg-[#1E293B]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-16 sm:py-24 text-center">
          <Reveal>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("appPage.eyebrow")}</div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-white text-balance">
              {standalone ? t("appPage.installedT") : t("appPage.installTitle")}
            </h1>
            <p className="mt-5 text-lg text-slate-400 max-w-2xl mx-auto">
              {standalone ? t("appPage.installedB") : t("appPage.installSub")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col items-center gap-4">
              {standalone ? (
                <div data-testid="app-installed-badge" className="inline-flex items-center gap-3 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 px-6 py-3">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-mono-t text-[11px] font-bold uppercase tracking-[0.2em]">{t("appPage.installedT")}</span>
                </div>
              ) : promptable ? (
                <button data-testid="app-install-btn" onClick={() => promptInstall()}
                  className="inline-flex items-center gap-3 rounded-full bg-[#FF6B35] text-white px-8 py-4 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] hover:scale-[1.03] transition-all shadow-lg shadow-orange-900/40">
                  <Download className="w-5 h-5" />
                  {t("appPage.install")}
                </button>
              ) : (
                <div data-testid="app-install-manual" className="max-w-lg rounded-3xl bg-white/5 border border-white/10 p-6 text-left">
                  {ios ? (
                    <div className="flex items-start gap-4">
                      <Share className="w-6 h-6 text-[#FF6B35] shrink-0 mt-0.5" />
                      <div>
                        <div className="font-display font-bold text-white">{t("appPage.iosT")}</div>
                        <p className="mt-1 text-sm text-slate-400 leading-relaxed">{t("appPage.iosB")}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <Download className="w-6 h-6 text-[#FF6B35] shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-400 leading-relaxed">{t("appPage.manualB")}</p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-500">{t("appPage.nativeSoon")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Vantaggi app */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-16 sm:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {perks.map(([icon, title, text], i) => (
            <Reveal key={title} delay={i * 0.07}>
              <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-7 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
                <span className="w-11 h-11 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center mb-5">{icon}</span>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.35}>
            <div className="rounded-3xl bg-[#E6F7F5] p-7 h-full flex flex-col justify-between">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{t("appPage.webT")}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t("appPage.webD")}</p>
              </div>
              <Link to="/destinations" data-testid="app-web-cta"
                className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors">
                {t("featured.viewAll")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
